<template>
  <div class="regional-selector-container is-flex" :class="{'small': isHeader}">
    <div class="edit-container" v-bind:class="{ 'adjust-container' : showEditButton }">
      <div v-if="!edit" class="edit-logo">
        <img v-if="!regionLogo" :src="regionIcon" alt="test">
        <img v-else-if="regionLogo" :src="regionLogo" alt="test">
      </div>
      <div v-if="showEditButton" class="edit-button">
        <button class="button is-outlined is-primary is-small" @click="editLogo()">
          <span><i class="fal fa-pencil-alt" aria-hidden="true"></i>&nbsp;<span class="editText">{{$t('edit')}}</span></span>
        </button>
      </div>
      <div v-if="showImageSave" class="image-upload-container">
        <div class="proxy-image" v-bind:style="proxiedRegionImg"></div>
        <image-upload class="logo-image" :class="{'is-hidden': !edit}" ref="logoUpload" :image="regionLogo" :callback="setLogo" :isPin="true" proxyWidth="100" proxyHeight="100"></image-upload>
      </div>
      <div v-if="showImageSave" class="flex-item">
        <button class="button is-primary is-small save-cancel" :disabled="isHidden" @click="saveRegionImg()">{{$t('save')}}</button>
        <button class="button is-primary is-small is-outlined" @click="cancel()">{{$t('cancel')}}</button>
      </div>
    </div>
    <div class="dropdown" v-bind:class="{'dropdown-margin' : edit}">
      <div class="select" role="search">
        <label for="select_region" class="visually-hidden">Regions:</label>
        <select v-model="regionDropDown" id="select_region" name="select_region"
                :class="{'small': isHeader}" :placeholder="selectRegionMessage"
                @change="updateRegion">
          <option :value="null" disabled>{{ selectRegionMessage }} </option>
          <optgroup v-for="province in provinces" :key="province.id" :label="province.name">
            <option v-for="region in province.regions" :key="region.id" class="option" :value="region">{{ region.region }}</option>
          </optgroup>
        </select>
      </div>
    </div>
  </div>
</template>

<script>
import i18n from '@/i18n'
import ImageUpload from '@/components/Inputs/ImageUpload'
import imageProxy from '@/libs/image-proxy'
import auth from '@/mixins/auth'

export default {
  name: 'regionDropdown',
  components: {
    ImageUpload
  },
  mixins: [auth],
  props: ['isHeader', 'selectedRegion'],
  data: function () {
    return {
      regionDropDown: {},
      loadingProjects: false,
      loadingRegions: false,
      error: '',
      edit: false,
      regionLogo: '',
      isRegionAdmin: false,
      isHidden: false
    }
  },
  mounted: async function () {
    this.loadingRegions = true
    try {
      await this.$store.dispatch('getRegions')
    } catch (err) {
      this.error = 'There seems to be an issue. Please reload the page and try again'
    }
    this.loadingRegions = false
    this.regionDropDown = this.selectedRegion
  },
  computed: {
    regions: function () {
      return this.$store.getters.regions ? this.$store.getters.regions : []
    },
    provinces: function () {
      const provs = this.$store.getters.provinces ? this.$store.getters.provinces : []
      if (provs.length === 0) {
        return []
      }

      return provs.filter(function (el) {
        return el.regions.length > 0
      })
    },
    regionIcon: function () {
      return this.regionDropDown?.image || '/static/ysc/locator-pin.png'
    },
    selectRegionMessage: function () {
      return i18n.t('regions.select')
    },
    regionImageBackground: function () {
      const style = {}
      style.backgroundImage = `url('${this.proxiedRegionImg}')`
      return style
    },
    proxiedRegionImg: function () {
      return this.regionIcon ? imageProxy.proxy(this.regionIcon, { width: 50, height: 50 }) : ''
    },
    loggedInUser: function () {
      return this.$store.getters.currentUser
    },
    showEditButton: function () {
      return this.selectedRegion && !this.edit && this.isRegionAdmin && this.isLoggedIn
    },
    showImageSave: function () {
      return this.edit && this.isLoggedIn && this.isRegionAdmin
    }
  },
  watch: {
    selectedRegion: function () {
      this.regionLogo = this.selectedRegion.image
      this.checkRegionAdmin()
    },
    isLoggedIn: function () {
      this.checkRegionAdmin()
    }
  },
  methods: {
    updateRegion: function () {
      this.$emit('updateRegion', this.regionDropDown)
    },
    editLogo: function () {
      this.regionLogo = this.regionIcon
      this.edit = true
    },
    cancel: function () {
      this.regionLogo = this.regionIcon
      this.edit = false
    },
    saveRegionImg: function () {
      this.isHidden = true
      this.$refs.logoUpload.upload('logos', this.$partner + this.selectedRegion.id).then((logoUrl) => {
        const params = { regionID: this.selectedRegion.id, logoURL: logoUrl }
        this.$store.dispatch('updateRegionLogo', params).then((response) => {
          this.setLogo(logoUrl)
          this.selectedRegion = response.region
          this.selectedRegion.regionAdmins = response.regionAdmins
          this.edit = false
          this.isHidden = false
        }, () => {
          this.error = 'Unable to upload Logo'
        })
      }).catch(() => {
        console.error(`Logo Image:: Unable to upload Logo for Region - ${this.selectedRegion.region}`)
        this.error = 'Unable to upload Logo image'
      })
    },
    setLogo: function (logo) {
      this.regionLogo = logo
    },
    checkRegionAdmin: function () {
      this.isRegionAdmin = false
      this.edit = false
      if (this.selectedRegion !== null && this.selectedRegion.regionAdmins !== null) {
        this.selectedRegion.regionAdmins.forEach(admin => {
          if (admin.userID === this.loggedInUser.id) {
            this.isRegionAdmin = true
          }
        })
      }
      if (this.isAdmin) {
        this.isRegionAdmin = true
      }
      if (this.isRegionAdmin || this.isAdmin) {
        return true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';
  .regional-selector-container {
    flex: 0 1 50%;
    max-width: 600px;
    align-items: center;
    margin-right: 10px;
    margin-top: -0.5rem;
    &.small {
      max-width: 400px;
    }
    @include touch {
      flex: 1 1 100%;
      flex-direction: column;
      max-width: 100%;
      margin-right: 0;
      margin-bottom: 20px;
    }
    img {
      max-width: 100px;
      height: 100px;
      object-fit: cover;
      margin-right: -2rem;
      border-radius: 50%;
      &.small{
        max-width: 30px;
        height: 30px;
      }
      @include touch {
        margin-right: 0;
        margin-bottom: 1rem;
      }
    }
    select,
    .select {
      width: 100%;
      height: 3.15rem;
      &.small{
        margin-top: 0.5rem;
        height: 2rem;
      }
    }
    select {
      font-size: 0.85rem;
      border-radius: 0;
      border: none;
    }
  }
.logo-image {
  border-radius: 50%;
  height: 100px;
  max-height: 100px;
  max-width: 100px;
  top: -3.5rem;
  left: 0.75rem;
}
.edit-container {
  padding-left: 4rem;
  @include mobile {
    padding-right: 3rem;
  }
  @include tablet {
    padding-right: 1rem;
  }
}
.edit-button {
  padding-bottom: 1rem;
  padding-left: 1rem;
  padding-top: 1rem;
}
.edit-logo {
  height: 100%;
  padding-top: 0.5rem;
  @include tablet {
  padding-right: 3rem;
  height: 65%;
  }
  @include mobile {
    height: 65%;
  }
}
.dropdown-margin {
  @include mobile {
    padding-right: 1rem;
  }
}
.flex-item {
  display: flex;
  @include mobile {
    margin-bottom: 1rem;
  }
  @include tablet {
    margin-bottom: 1rem;
  }
}
.save-cancel {
  margin-right: 1rem;
}
.proxy-image {
  height: 70px;
}
.image-upload-container {
  max-height: 130px;
}
.adjust-container {
  margin-top: 2.5rem;
}
</style>
